import { PermissionGate } from "@components/PermissionGate";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Modal,
  NumberInput,
  ScrollAreaAutosize,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import i18n from "src/i18n";
import { z } from "zod";

import { useUpdateClinicTreatmentPlan } from "@hooks/mutations/clinic/useUpdateClinicTreatmentPlan";

import { ClinicTreatmentPlan } from "@customTypes/clinic";

import { StyledEditTreatmentPlanModalContent } from "./EditTreatmentPlanModal.styled";


const editFormSchema = () =>
  z.object({
    name: z.string().min(1, i18n.t("name-required")),
    category: z.string().optional(),
    price: z.number().min(1, i18n.t("price-required")),
  });

type EditFormInputs = z.infer<ReturnType<typeof editFormSchema>>;

interface EditTreatmentPlanModalProps {
  opened: boolean;
  close: () => void;
  treatmentPlan: ClinicTreatmentPlan | null;
}

export const EditTreatmentPlanModal = ({
  opened,
  close,
  treatmentPlan,
}: EditTreatmentPlanModalProps) => {
  const { t } = useTranslation();

  const {
    mutate: updateTreatmentPlan,
    isLoading: isUpdateTreatmentPlanLoading,
  } = useUpdateClinicTreatmentPlan();

  const form = useForm({
    mode: "uncontrolled",
    validate: zodResolver(editFormSchema()),
    initialValues: {
      name: "",
      category: "",
      price: 0,
    },
  });

  // Update form values when treatmentPlan changes
  useEffect(() => {
    if (treatmentPlan && opened) {
      form.setValues({
        name: treatmentPlan.name || "",
        category: treatmentPlan.category || "",
        price: treatmentPlan.price || 0,
      });
    }
  }, [treatmentPlan, opened]);

  // Reset form when modal is closed
  useEffect(() => {
    if (!opened) {
      form.reset();
    }
  }, [opened]);

  const handleFormSubmit = (values: EditFormInputs) => {
    if (!treatmentPlan?.id) return;

    updateTreatmentPlan(
      {
        treatmentPlanId: treatmentPlan.id,
        treatmentPlan: values,
      },
      {
        onSuccess: () => {
          handleClose();
        },
      }
    );
  };

  const handleClose = () => {
    form.reset();
    close();
  };

  return (
    <Modal
      centered
      title={<Text fw={500}>{t("edit-treatment-plan")}</Text>}
      opened={opened}
      onClose={handleClose}
      scrollAreaComponent={ScrollAreaAutosize}
      size="md">
      <StyledEditTreatmentPlanModalContent>
        <form
          className="form"
          onSubmit={form.onSubmit(
            values => handleFormSubmit(values),
            errors => {
              const firstErrorPath = Object.keys(errors)[0];
              form.getInputNode(firstErrorPath)?.focus();
            }
          )}>
          <div className="form-field">
            <TextInput
              label={t("name")}
              withAsterisk
              {...form.getInputProps("name")}
              key={form.key("name")}
              placeholder={t("eg", { example: "Physical Therapy" })}
            />
          </div>

          <div className="form-field">
            <TextInput
              label={t("category")}
              {...form.getInputProps("category")}
              key={form.key("category")}
              placeholder={t("category")}
            />
          </div>

          <div className="form-field">
            <NumberInput
              label={t("price")}
              withAsterisk
              {...form.getInputProps("price")}
              key={form.key("price")}
              min={0}
              step={1}
              placeholder="15000"
            />
          </div>

          <div className="form-actions">
            {/* <Button
              variant="outline"
              onClick={handleClose}
              disabled={isUpdateTreatmentPlanLoading}>
              {t("cancel")}
            </Button> */}
            <PermissionGate permission="resources.edit"><Button
              type="submit"
              loading={isUpdateTreatmentPlanLoading}>
              {t("update")}
            </Button></PermissionGate>
          </div>
        </form>
      </StyledEditTreatmentPlanModalContent>
    </Modal>
  );
};
